/**
 * Cashflow tab for v2. Entirely computed — every line derives from the P&L,
 * the control accounts and the fixed asset schedule.
 *
 * Three lines exist here that the template's cashflow does not have, and they
 * are why the balance sheet balances:
 *
 * - Change in Stocks
 * - stock, VAT and deferred tax movements inside Change in Other Debtors
 * - Net Income taken as profit AFTER TAX rather than retained profits
 *
 * The template started from retained profits, which is already net of
 * dividends, and then deducted dividends again in financing. It also omitted
 * the stock, VAT and deferred tax movements entirely, so its cashflow and
 * balance sheet disagreed as soon as any of them moved. See spec 7.5.
 */

import { ComputedDataRow } from '../table/DataRow';
import { GrandTotalRow } from '../table/GrandTotalRow';
import { SectionHeader } from '../table/SectionHeader';
import { SubtotalRow } from '../table/SubtotalRow';
import { useCallback } from 'react';
import { useFinancialModelV2Store } from '../../../store/financial-model-v2';
import { V2EditableRow } from './V2EditableRow';
import { V2Sheet } from './V2Table';
import type { PeriodData } from '../../../types/financial-model-v2';

/** Where the printed cash flow statement was proved and the model's change in cash is the
 *  printed one, the filing's split between the sections: what the model derived elsewhere,
 *  moved to where the filing puts it. Shown only where it is not nought. */
function Reclassified({ data }: { data: PeriodData | undefined }) {
  if (!data || !Object.values(data).some((v) => v)) return null;
  return <ComputedDataRow label="Reclassified to the printed cash flow statement" data={data} />;
}

export function CashflowViewV2() {
  const model = useFinancialModelV2Store((s) => s.model);
  const updateInput = useFinancialModelV2Store((s) => s.updateInput);
  const handleChange = useCallback(
    (path: string) => (period: string, value: number) => updateInput(path, period, value),
    [updateInput],
  );
  if (!model) return null;
  const cf = model.computed.cashflow;

  return (
    <V2Sheet>
      <SectionHeader label="Operating Activities" />
      <ComputedDataRow label="Net Income (profit after tax)" data={cf.netIncome} />
      <ComputedDataRow label="Add back: Depreciation & Amortisation" data={cf.addBackDepnAmort} />
      <ComputedDataRow label="Change in Stocks" data={cf.changeInStocks} />
      <ComputedDataRow label="Change in Trade Debtors" data={cf.changeInTradeDebtors} />
      <ComputedDataRow
        label="Change in Other Debtors, VAT & Deferred Tax"
        data={cf.changeInOtherDebtors}
      />
      <ComputedDataRow label="Change in Trade Creditors" data={cf.changeInTradeCreditors} />
      <ComputedDataRow label="Change in Other Creditors" data={cf.changeInOtherCreditors} />
      {/* Provisions, contract liabilities, accruals and anything else
          declared. Their effect was already inside the total below; without
          a row of its own the total did not explain itself. */}
      <ComputedDataRow
        label="Change in Other Working Capital"
        data={cf.changeInDeclaredWorkingCapital ?? {}}
      />
      {/* IAS 7 paragraph 31 asks for these separately. Both were buried
          inside net income. They are a disclosure of what it contains, not
          an addition to it. */}
      <ComputedDataRow label="— of which interest received" data={cf.interestReceived ?? {}} />
      <ComputedDataRow label="— of which interest paid" data={cf.interestPaid ?? {}} />
      <ComputedDataRow
        label="— of which dividends received"
        data={cf.dividendsReceived ?? {}}
      />
      <ComputedDataRow label="Corporation Tax" data={cf.corporationTaxPaid} />
      <Reclassified data={cf.printedReclassification?.operating} />
      <SubtotalRow label="Net Operating Cashflow" data={cf.netOperatingCashflow} />

      <SectionHeader label="Investing Activities" />
      <ComputedDataRow label="Fixed Asset Purchases" data={cf.fixedAssetPurchases} />
      <ComputedDataRow
        label="Fixed Asset Disposal Proceeds"
        data={cf.fixedAssetDisposalProceeds ?? {}}
      />
      {/* Investments, investment property and assets held for sale. These
          used to land in operating for want of a third section. */}
      <ComputedDataRow
        label="Investments Bought and Sold"
        data={cf.changeInDeclaredInvesting ?? {}}
      />
      <Reclassified data={cf.printedReclassification?.investing} />
      <SubtotalRow label="Net Investing Cashflow" data={cf.netInvestingCashflow} />

      <SectionHeader label="Financing Activities" />
      <ComputedDataRow label="Equity Injection" data={cf.equityInjection} />
      <V2EditableRow
        label="Loan Drawdowns"
        data={model.inputs.debtSchedule?.loanDrawdowns ?? {}}
        onCellChange={handleChange('debtSchedule.loanDrawdowns')}
      />
      {/* Entered positive; the statement shows it as the outflow it is. */}
      <V2EditableRow
        label="Loan Repayments (enter positive)"
        data={model.inputs.debtSchedule?.loanRepayments ?? {}}
        onCellChange={handleChange('debtSchedule.loanRepayments')}
      />
      <ComputedDataRow label="Dividends Paid" data={cf.dividendsPaid} />
      {/* The capital half only. The interest half is already in the operating
          section through profit, and taking the whole payment out here would
          charge it twice. */}
      {Object.values(cf.leasePrincipalRepaid ?? {}).some((v) => v) && (
        <ComputedDataRow
          label="Lease principal repaid"
          data={cf.leasePrincipalRepaid ?? {}}
        />
      )}
      <ComputedDataRow
        label="Change in Borrowings and Leases"
        data={cf.changeInDeclaredBorrowings ?? {}}
      />
      <Reclassified data={cf.printedReclassification?.financing} />
      <SubtotalRow label="Net Financing Cashflow" data={cf.netFinancingCashflow} />

      {/* IAS 7 paragraph 28, outside the three sections because it is neither a
          receipt nor a payment: cash sits at the closing rate while the flows
          happened at the rates they happened at. Without it a company holding
          foreign currency cannot balance, because this model derives cash
          rather than reading it off the balance sheet. */}
      <V2EditableRow
        label="Effect of exchange rate changes on cash"
        data={model.inputs.exchangeEffectOnCash ?? {}}
        onCellChange={handleChange('exchangeEffectOnCash')}
      />

      <SectionHeader label="Cash" />
      <SubtotalRow label="Net Cashflow" data={cf.netCashflow} />
      <ComputedDataRow label="Opening Cash" data={cf.openingCash} />
      <GrandTotalRow label="Closing Cash" data={cf.closingCash} />
    </V2Sheet>
  );
}
